import { imageDataToBase64 } from '@/common/images';
import { useLayoutContext } from '@/contexts';
import { Flex, ScrollArea, Space, Text } from '@mantine/core';
import { IconCamera, IconCameraOff, IconCircleCheck, IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StandardButton } from '../buttons';
import { WorkflowFooter } from '../workflow/workflow-footer';
import styles from './styles.module.css';

let offscreenCanvas: OffscreenCanvas | undefined;

type CameraProps = {
	width?: number | string;
	height?: number | string;
} & (
	| {
			multiple?: false;
			onTakePhoto: (imageData: ImageData) => void;
	  }
	| {
			multiple?: true;
			onTakePhoto: (imageData: ImageData[]) => void;
	  }
);

type CapturedPhoto = {
	id: string;
	imageData: ImageData;
	preview: string;
};

export const Camera = ({ onTakePhoto, width, height, multiple = false }: CameraProps) => {
	const { isApp } = useLayoutContext();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [showFlash, setShowFlash] = useState(false);

	// Single photo mode state
	const [capturedImageData, setCapturedImageData] = useState<ImageData | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);

	// Multi photo mode state
	const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([]);
	const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);

	// Handler device not found
	const [deviceNotFound, setDeviceNotFound] = useState(false);

	// Kick off the camera
	useEffect(() => {
		(async () => {
			try {
				const mediaStream = await navigator.mediaDevices.getUserMedia({
					audio: false,
					video: {
						facingMode: 'environment',
						width: { ideal: 4096 },
						height: { ideal: 2160 },
						advanced: [
							{ width: { min: 1920 }, height: { min: 1080 } },
							{ width: { min: 1280 }, height: { min: 720 } },
							{ width: { min: 640 }, height: { min: 480 } },
						],
					},
				});

				if (!videoRef.current) throw new Error('Video ref is not set.');
				videoRef.current.srcObject = mediaStream;
			} catch {
				setDeviceNotFound(true);
			}
		})();
	}, []);

	const imageData = useRef<ImageData | undefined>(undefined);

	const handleTakePhoto = useCallback(() => {
		if (imageData.current) {
			// Trigger flash animation
			setShowFlash(true);
			setTimeout(() => setShowFlash(false), 150);

			if (multiple) {
				// Multi photo mode: add to list
				const id = `photo_${Date.now()}_${Math.random()}`;
				const base64 = imageDataToBase64(imageData.current, 'jpeg', 0.8);
				const preview = `data:image/jpeg;base64,${base64}`;

				const newPhoto: CapturedPhoto = {
					id,
					imageData: imageData.current,
					preview,
				};

				setCapturedPhotos((prev) => [...prev, newPhoto]);
			} else {
				// Single photo mode: show preview
				setCapturedImageData(imageData.current);
				const base64 = imageDataToBase64(imageData.current, 'jpeg', 0.8);
				setImagePreview(`data:image/jpeg;base64,${base64}`);
			}
		}
	}, [imageData, multiple]);

	const handleAcceptPhoto = useCallback(() => {
		if (multiple) {
			// Multi photo mode: send all photos
			const imageDataArray = capturedPhotos.map((photo) => photo.imageData);
			(onTakePhoto as (imageData: ImageData[]) => void)(imageDataArray);
		} else if (capturedImageData) {
			// Single photo mode: send single photo
			(onTakePhoto as (imageData: ImageData) => void)(capturedImageData);
		}
	}, [capturedImageData, capturedPhotos, onTakePhoto, multiple]);

	const handleDiscardPhoto = useCallback(() => {
		if (multiple && selectedPhotoId) {
			// Remove specific photo from multi photo list
			setCapturedPhotos((prev) => prev.filter((photo) => photo.id !== selectedPhotoId));
			setSelectedPhotoId(null);
		} else {
			// Single photo mode: discard current photo
			setCapturedImageData(null);
			setImagePreview(null);
		}
	}, [multiple, selectedPhotoId]);

	const handleThumbnailClick = useCallback((photoId: string) => {
		setSelectedPhotoId(photoId);
	}, []);

	const handleClosePreview = useCallback(() => {
		setSelectedPhotoId(null);
	}, []);

	// Get currently selected photo for preview
	const selectedPhoto = selectedPhotoId
		? capturedPhotos.find((photo) => photo.id === selectedPhotoId)
		: null;

	// Determine what preview to show
	const currentPreview = multiple ? selectedPhoto?.preview || null : imagePreview;

	// Kick off detection
	useEffect(() => {
		let animationHandle: number;

		const detect = async () => {
			const video = videoRef.current;
			if (!video) throw new Error('Video ref is not set.');

			if (video.videoHeight && video.videoWidth) {
				if (
					!offscreenCanvas ||
					offscreenCanvas.width !== offscreenCanvas.width ||
					offscreenCanvas.height !== offscreenCanvas.height
				) {
					// Only resizing the canvas caused Chromium to become progressively slower
					offscreenCanvas = new OffscreenCanvas(video.videoWidth, video.videoHeight);
				}

				const context = offscreenCanvas?.getContext('2d', { willReadFrequently: true });
				if (!context) throw new Error('Could not get 2d context from offscreen canvas.');
				context.drawImage(video, 0, 0);
				imageData.current = context.getImageData(
					0,
					0,
					offscreenCanvas.width,
					offscreenCanvas.height
				);
			}

			animationHandle = requestAnimationFrame(detect);
		};

		animationHandle = requestAnimationFrame(detect);

		return () => {
			cancelAnimationFrame(animationHandle);
		};
	}, [videoRef]);

	return (
		// This is a load bearing div. Please do not remove it, as it will break in iOS Safari
		// if this is not here.
		<div style={{ width, height }} className={styles.container}>
			{deviceNotFound ? (
				<div
					style={{
						height: '100vh',
						width: '100%',
						backgroundColor: 'rgb(0, 0, 0, 0.9)',
						position: 'fixed',
						top: 0,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						color: 'white',
						fontSize: 24,
						fontWeight: 600,
						textAlign: 'center',
						gap: 6,
					}}
				>
					<IconCameraOff strokeWidth={1.2} opacity={0.4} size={100} />
					<Space h="md" />
					No camera found
					<Text size="sm" opacity={0.4}>
						Please check your device settings and try again.
					</Text>
				</div>
			) : (
				<video
					ref={videoRef}
					muted
					playsInline
					disablePictureInPicture
					autoPlay
					className={styles.video}
					width="100%"
					height="100%"
				/>
			)}

			{/* Preview overlay */}
			{currentPreview && <img className={styles.previewImage} src={currentPreview} alt="Preview" />}

			{/* Flash animation overlay */}
			<AnimatePresence>
				{showFlash && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.1 }}
						className={styles.flashOverlay}
					/>
				)}
			</AnimatePresence>

			{/* Multi photo thumbnail list */}
			{multiple && capturedPhotos.length > 0 && !selectedPhotoId && (
				<div className={styles.thumbnailContainer} style={{ bottom: isApp ? 135 : 80 }}>
					<ScrollArea className={styles.thumbnailScrollArea}>
						<Flex gap="xs" className={styles.thumbnailList}>
							{capturedPhotos.map((photo) => (
								<div
									key={photo.id}
									className={styles.thumbnail}
									onClick={() => handleThumbnailClick(photo.id)}
								>
									<img src={photo.preview} alt="Thumbnail" className={styles.thumbnailImage} />
								</div>
							))}
						</Flex>
					</ScrollArea>
				</div>
			)}

			{/* Camera controls */}
			<AnimatePresence>
				{(!currentPreview || (multiple && !selectedPhotoId)) && (
					<motion.div
						initial={{ opacity: 0, y: 100 }}
						animate={{
							opacity: 1,
							y: 0,
							transition: {
								type: 'spring',
								damping: 25,
								stiffness: 200,
								duration: 0.6,
								delay: 0.5,
							},
						}}
						exit={{
							opacity: 0,
							y: 100,
							transition: {
								duration: 0.3,
								ease: 'easeInOut',
							},
						}}
						className={styles.controlsContainer}
					>
						<WorkflowFooter>
							{multiple && capturedPhotos.length > 0 && (
								<>
									<StandardButton
										fullWidth
										disabled={capturedPhotos.length === 0}
										onClick={handleAcceptPhoto}
									>
										<IconCircleCheck size={23} />
										<Space w="xs" />
										Accept ({capturedPhotos.length})
									</StandardButton>
									<StandardButton
										fullWidth
										bg="grey"
										c={deviceNotFound ? 'black' : 'white'}
										opacity={deviceNotFound ? 0.4 : 1}
										loading={false}
										disabled={deviceNotFound}
										onClick={handleTakePhoto}
									>
										{deviceNotFound ? <IconCameraOff /> : <IconCamera />}
										<Space w="xs" />
										Take Photo
									</StandardButton>
								</>
							)}
							{(!multiple || capturedPhotos.length === 0) && (
								<StandardButton
									fullWidth
									bg="grey"
									c={deviceNotFound ? 'black' : 'white'}
									opacity={deviceNotFound ? 0.4 : 1}
									loading={false}
									disabled={deviceNotFound}
									onClick={handleTakePhoto}
								>
									{deviceNotFound ? <IconCameraOff /> : <IconCamera />}
									<Space w="xs" />
									Take Photo
								</StandardButton>
							)}
						</WorkflowFooter>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Preview controls */}
			<AnimatePresence>
				{currentPreview && (!multiple || selectedPhotoId) && (
					<motion.div
						initial={{ opacity: 0, y: 100 }}
						animate={{
							opacity: 1,
							y: 0,
							transition: {
								type: 'spring',
								damping: 25,
								stiffness: 200,
								duration: 0.6,
								delay: 0.1,
							},
						}}
						exit={{
							opacity: 0,
							y: 100,
							transition: {
								duration: 0.3,
								ease: 'easeInOut',
							},
						}}
						className={styles.controlsContainer}
					>
						<Flex align="center" justify="center" gap="sm" className={styles.previewControlsPanel}>
							{multiple && selectedPhotoId ? (
								<>
									<StandardButton onClick={handleDiscardPhoto} fullWidth variant="default">
										<IconX size={18} />
										<Space w="xs" />
										Remove Photo
									</StandardButton>
									<StandardButton onClick={handleClosePreview} fullWidth>
										Continue Taking Photos
									</StandardButton>
								</>
							) : (
								<>
									<StandardButton onClick={handleDiscardPhoto} fullWidth variant="default">
										Try Again
									</StandardButton>
									<StandardButton onClick={handleAcceptPhoto} fullWidth>
										Accept Photo
									</StandardButton>
								</>
							)}
						</Flex>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
